/**
 * Typed errors raised by the API client. Components catch these to decide
 * whether to show a login screen, an inline error, or a toast.
 *
 * Tokens never appear here: the BFF/cookie strategy means the frontend never
 * touches access/refresh tokens.
 */

export class ApiError extends Error {
  readonly status: number
  readonly detail: string
  readonly payload: unknown

  constructor(message: string, status: number, detail: string, payload: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.detail = detail
    this.payload = payload
  }
}

/** Raised when the user is unauthenticated or the session has expired. */
export class UnauthorizedError extends ApiError {
  constructor(detail: string, payload: unknown) {
    super(detail || 'Não autenticado', 401, detail, payload)
    this.name = 'UnauthorizedError'
  }
}

/** Network failure (browser couldn't reach the backend at all). */
export class NetworkError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'NetworkError'
  }
}

/**
 * Raised when a request exceeds its client-side timeout budget and is aborted
 * by the client (as opposed to a caller-initiated cancellation, which keeps
 * surfacing as a plain ``AbortError``). Components treat it like any other
 * ``Error`` — ``message`` is already user-readable — so a hung mutation can
 * never leave a spinner (e.g. a delete modal's "Excluindo…") stuck forever.
 */
export class TimeoutError extends Error {
  readonly timeoutMs: number

  constructor(message: string, timeoutMs: number) {
    super(message)
    this.name = 'TimeoutError'
    this.timeoutMs = timeoutMs
  }
}
